import { ColumnFiltersState } from '@tanstack/react-table';
import {
	AccountStatus,
	CustomDetailDataTypeEnum,
	CustomDetailFilterDto,
	InvestorsFilterQuery,
	ProgrammeStatus,
	InvestorEligibilityFilter,
} from '@dealstack/shared/lib/generated';

// Custom-detail column ids are self-describing: `customDetail:<dataType>:<customDetailId>`. Encoding
// the dataType in the id lets us build the right filter shape from column state alone, without
// depending on the list response (which is itself keyed by the filter — that would be circular).
const CUSTOM_DETAIL_COLUMN_PREFIX = 'customDetail:';

/**
 * Merges quick filters and column-level filters into a single
 * InvestorsFilterQuery for the investor list API.
 *
 * Precedence (last wins): quick filters < column filters.
 */
export const mergeInvestorsFilterQuery = (
	quickFilters: Record<string, boolean | string>,
	columnFilters: ColumnFiltersState,
	eligibilityFilter?: InvestorEligibilityFilter | null
): InvestorsFilterQuery | undefined => {
	let result: InvestorsFilterQuery | undefined;

	// 1. Merge quick filters for programmeStatuses
	const quickProgrammeStatuses = quickFilters.programmeStatuses;
	if (quickProgrammeStatuses && typeof quickProgrammeStatuses === 'string') {
		const statuses = quickProgrammeStatuses.split(',') as ProgrammeStatus[];
		result = {
			...result,
			programmeStatuses: statuses,
		};
	}

	const customDetailFilters: CustomDetailFilterDto[] = [];

	// 2. Merge column-level filters (take precedence)
	for (const cf of columnFilters) {
		if (cf.id === 'accountStatus' && Array.isArray(cf.value) && cf.value.length > 0) {
			result = {
				...result,
				accountStatuses: cf.value as AccountStatus[],
			};
		}
		if (cf.id === 'programmeStatus' && Array.isArray(cf.value) && cf.value.length > 0) {
			const statuses = cf.value as ProgrammeStatus[];
			result = {
				...result,
				programmeStatuses: statuses,
			};
		}
		if (cf.id === 'investor' && Array.isArray(cf.value) && cf.value.length > 0) {
			result = {
				...result,
				investorIds: cf.value as string[],
			};
		}
		if (cf.id === 'investmentVehicles' && Array.isArray(cf.value) && cf.value.length > 0) {
			result = {
				...result,
				pivPersonsIds: cf.value as string[],
			};
		}
		if (cf.id === 'mipClassifiers' && Array.isArray(cf.value) && cf.value.length > 0) {
			result = {
				...result,
				mipClassifierIds: cf.value as string[],
			};
		}
		if (cf.id === 'workInProgress' && (cf.value === 'true' || cf.value === 'false')) {
			result = {
				...result,
				hasActiveInvestmentWorkflow: cf.value === 'true',
			};
		}
		if (cf.id.startsWith(CUSTOM_DETAIL_COLUMN_PREFIX)) {
			const [, dataType, customDetailId] = cf.id.split(':');

			if (customDetailId && dataType === CustomDetailDataTypeEnum.BOOLEAN) {
				if (cf.value === 'true' || cf.value === 'false') {
					customDetailFilters.push({ customDetailId, boolean: cf.value === 'true' });
				}
			} else if (customDetailId && typeof cf.value === 'string' && cf.value.length > 0) {
				customDetailFilters.push({ customDetailId, text: cf.value });
			}
		}
		if (cf.id === 'countryOfBirth' && typeof cf.value === 'string' && cf.value.length > 0) {
			result = {
				...result,
				countryOfBirth: cf.value,
			};
		}
		if (cf.id === 'taxResidenceTin' && typeof cf.value === 'string' && cf.value.length > 0) {
			result = {
				...result,
				taxResidenceSearch: cf.value,
			};
		}
		if (cf.id === 'citizenships' && Array.isArray(cf.value) && cf.value.length > 0) {
			result = {
				...result,
				citizenships: cf.value as string[],
			};
		}
		if (cf.id === 'employeeIds' && typeof cf.value === 'string' && cf.value.length > 0) {
			result = {
				...result,
				employeeIdSearch: cf.value,
			};
		}
	}

	if (customDetailFilters.length > 0) {
		result = {
			...result,
			customDetailFilters,
		};
	}

	// 3. Apply eligibility filter for bulk actions
	if (eligibilityFilter) {
		result = {
			...result,
			eligibilityFilter,
		};
	}

	return result;
};
